import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { hasModuleAccess } from "../../accessControl";
import { useAuth } from "../../useAuth";
import { RisksService } from "../../api/generated";
import { withApiData } from "../../api/openapiClient";
import { linkControlEntity, unlinkControlEntity } from "../../services/controlLinks";
import DestructiveConfirm from "../common/DestructiveConfirm";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";

const PAGE_SIZE = 20;
const reference = (risk) => risk.risk_code || `Risk #${risk.id}`;
const failureMessage = (error) => {
  if (error?.status === 403) return "You do not have permission to change these Risk relationships.";
  if (error?.status === 404) return "The Control or Risk is no longer accessible. Refresh and try again.";
  return error?.message || "Unable to update linked Risks. Please try again.";
};

// Display the actor-filtered projection, never raw relationship IDs.
export default function ControlRisksPanel({ control, editing = false, onChange }) {
  const { user } = useAuth() || {};
  const canRead = hasModuleAccess(user, "risks", "VIEW");
  const archived = String(control?.status || "").trim().toLowerCase() === "archived";
  const canEdit = editing && !archived && canRead && hasModuleAccess(user, "controls", "EDIT");
  const risks = canRead ? control?.linked_risks || [] : [];
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setOffset(0); }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setSelected(""); setConfirmUnlink(null); setError(""); setNotice("");
  }, [control?.id, canEdit]);

  const candidates = useQuery({
    queryKey: ["controlRiskCandidates", control?.id, user?.id, query, offset],
    queryFn: () => withApiData(RisksService.listRisksRisksGet({ q: query, limit: PAGE_SIZE, offset })),
    enabled: Boolean(control?.id && canEdit),
    retry: false,
  });
  const choices = (candidates.data?.items || []).filter(risk =>
    !risks.some(linked => linked.id === risk.id));
  const change = async (kind, risk) => {
    if (!canEdit || busy.current || !risk) return;
    if (kind === "add" && risks.some(linked => linked.id === risk.id)) return;
    if (kind === "remove" && risks.length <= 1) return;
    busy.current = true; setPending(true); setError(""); setNotice("");
    try {
      if (kind === "add") await linkControlEntity(control.id, "risks", risk.id);
      else await unlinkControlEntity(control.id, "risks", risk.id);
      window.dispatchEvent(new CustomEvent("control-risk-links-changed", {
        detail: { controlId: control.id, riskId: risk.id },
      }));
      setSelected(""); setConfirmUnlink(null);
      await onChange?.(risk.id);
      setNotice(kind === "add" ? "Risk linked." : "Risk unlinked.");
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      busy.current = false; setPending(false);
    }
  };

  return (
    <section aria-label="Linked Risks" style={{ display: "grid", gap: 12 }}>
      <h3 style={{ margin: 0 }}>Linked Risks</h3>
      {!canRead && <p>Linked Risks are not accessible with your current permissions.</p>}
      {canRead && risks.length === 0 && <p>No accessible linked Risks. Refresh if you expect a relationship.</p>}
      {risks.map(risk => (
        <div key={risk.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
          <Link to={`/risks/${risk.id}`}>{risk.name}</Link>
          <span style={{ marginLeft: 8 }}>{reference(risk)}</span>
          {canEdit && (
            <Button variant="destructive" disabled={pending || risks.length <= 1}
              aria-label={`Unlink ${risk.name}`} onClick={() => setConfirmUnlink(risk)}>
              Unlink
            </Button>
          )}
        </div>
      ))}
      {canEdit && (
        <div style={{ display: "grid", gap: 8 }}>
          <p>Link existing Risks. Each change is saved immediately; other Control edits are saved separately.</p>
          <Input label="Search existing Risks" value={search} disabled={pending}
            onChange={event => { setSearch(event.target.value); setSelected(""); }} />
          {candidates.isFetching && <div role="status">Searching Risks...</div>}
          {candidates.isError && <div role="alert">Unable to load accessible Risks. {failureMessage(candidates.error)}</div>}
          <Select label="Select a Risk to link" value={selected} disabled={pending || candidates.isFetching || candidates.isError}
            onChange={event => setSelected(event.target.value)}>
            <option value="">Select an existing Risk</option>
            {choices.map(risk => <option key={risk.id} value={risk.id}>{risk.name} — {reference(risk)}</option>)}
          </Select>
          {!candidates.isFetching && !candidates.isError && choices.length === 0 &&
            <p>No unlinked Risks on this page. Try another search or page.</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={pending || candidates.isFetching || candidates.isError || !choices.some(r => String(r.id) === selected)}
              onClick={() => change("add", choices.find(r => String(r.id) === selected))}>Link Risk</Button>
            <Button variant="secondary" disabled={pending || candidates.isFetching || offset === 0}
              onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelected(""); }}>Previous Risks</Button>
            <Button variant="secondary" disabled={pending || candidates.isFetching || offset + PAGE_SIZE >= (candidates.data?.total || 0)}
              onClick={() => { setOffset(offset + PAGE_SIZE); setSelected(""); }}>More Risks</Button>
          </div>
          {risks.length <= 1 && <p>A Control must retain at least one Risk. Link another Risk before unlinking this one.</p>}
        </div>
      )}
      {canEdit && confirmUnlink && (
        <DestructiveConfirm actionId="CONTROL_LINK_UNLINK" open resourceDisplayName={`${confirmUnlink.name} — ${reference(confirmUnlink)}`}
          variantOverride="inline" onCancel={() => !pending && setConfirmUnlink(null)}
          onConfirm={() => change("remove", confirmUnlink)} />
      )}
      {error && <div role="alert">{error}</div>}
      {notice && <div role="status">{notice}</div>}
    </section>
  );
}
